// Importaciones de interfaces.
import { ICustomer } from '../interfaces/customer.interface';

/**
 * Clase que representa a un cliente.
 *
 * Esta clase implementa la interfaz `ICustomer` y define las propiedades y el
 * constructor necesarios para representar los datos de un cliente.
 * Un cliente tiene información personal como su identificador, referencia,
 * nombre, dirección, teléfono, entre otros.
 */
export class Customer implements ICustomer {
  /**
   * Identificador único del cliente.
   *
   * Este campo puede ser un número que representa el ID del cliente o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Referencia asociada al cliente.
   *
   * Este campo puede ser un string que representa la referencia única del cliente o nulo si no está asignado.
   */
  reference: string | null;

  /**
   * NIF o documento de identificación fiscal del cliente.
   *
   * Este campo puede ser un string que representa el NIF del cliente o nulo si no está asignado.
   */
  nif: string | null;

  /**
   * Nombre del cliente.
   *
   * Este campo puede ser un string que representa el nombre del cliente o nulo si no está asignado.
   */
  name: string | null;

  /**
   * Nombre comercial.
   *
   * Este campo puede ser una cadena que representa el nombre comercial
   * del cliente, o nulo si no está disponible.
   */
  tradeName: string | null;

  /**
   * Dirección del cliente.
   *
   * Este campo puede ser un string que representa la dirección del cliente o nulo si no está asignado.
   */
  address: string | null;

  /**
   * Teléfono del cliente.
   *
   * Este campo puede ser un string que representa el número de teléfono del cliente o nulo si no está asignado.
   */
  telephone: string | null;

  /**
   * Ciudad del cliente.
   *
   * Este campo puede ser un string que representa la ciudad del cliente o nulo si no está asignado.
   */
  city: string | null;

  /**
   * Provincia del cliente.
   *
   * Este campo puede ser un string que representa la provincia del cliente o nulo si no está asignado.
   */
  province: string | null;

  /**
   * Código postal del cliente.
   *
   * Este campo puede ser un string que representa el código postal del cliente o nulo si no está asignado.
   */
  postalCode: string | null;

  /**
   * País del cliente.
   *
   * Este campo puede ser un string que representa el país del cliente o nulo si no está asignado.
   */
  country: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de ICustomer.
   *
   * @param {Partial<ICustomer>} customer - Un objeto parcial de ICustomer con las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador único del cliente. Por defecto, `null`.
   *   - `reference` (opcional): Referencia interna del cliente. Por defecto, `null`.
   *   - `nif` (opcional): Número de identificación fiscal (NIF/CIF/DNI) del cliente. Por defecto, `null`.
   *   - `name` (opcional): Nombre completo del cliente. Por defecto, `null`.
   *   - `tradeName` (opcional): Nombre comercial del cliente. Por defecto, `null`.
   *   - `address` (opcional): Dirección del cliente. Por defecto, `null`.
   *   - `telephone` (opcional): Número de teléfono del cliente. Por defecto, `null`.
   *   - `city` (opcional): Ciudad de residencia del cliente. Por defecto, `null`.
   *   - `province` (opcional): Provincia de residencia del cliente. Por defecto, `null`.
   *   - `postalCode` (opcional): Código postal del cliente. Por defecto, `null`.
   *   - `country` (opcional): País de residencia del cliente. Por defecto, `null`.
   */
  constructor({
    id = null,
    reference = null,
    nif = null,
    name = null,
    tradeName = null,
    address = null,
    telephone = null,
    city = null,
    province = null,
    postalCode = null,
    country = null,
  }: Partial<ICustomer> = {}) {
    this.id = id;
    this.reference = reference;
    this.nif = nif;
    this.name = name;
    this.tradeName = tradeName;
    this.address = address;
    this.telephone = telephone;
    this.city = city;
    this.province = province;
    this.postalCode = postalCode;
    this.country = country;
  }
}
